import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useRef } from 'react';
import type { ApiClient } from './api-client';
import { applySlotChanges, mySlotId, type Board } from './board';
import type { ConnectionState } from './stay-sync';
import { useStayEvents } from './use-stay-events';

export interface LiveBoard {
  readonly board: UseQueryResult<Board>;
  readonly connection: ConnectionState;
  /** Re-reads the board and everything derived from it (the caller's `alsoInvalidate` keys). */
  readonly resync: () => void;
}

/**
 * The board of a stay, kept live: one read + the SSE stream applying in-order slot changes to the query cache and
 * re-reading on anything else (REALTIME_DESIGN.md §7–§8). Used by every screen that shows a board — passenger,
 * guardian, staff, hotel and coordinator — so they all behave the same.
 *
 * `alsoInvalidate`: query keys that depend on the distribution (e.g. "my assignment", "progress"); they are re-read
 * on every resync and whenever a change touches the viewer's own slot (`onMineTouched`) or any slot (`onAnyChange`).
 */
export function useLiveBoard(
  api: ApiClient,
  stayId: string,
  options: {
    readonly alsoInvalidate?: readonly (readonly unknown[])[];
    /** Re-read `alsoInvalidate` on EVERY applied change (staff progress panels), not only when my own slot changed. */
    readonly invalidateOnAnyChange?: boolean;
    readonly onSessionEnded?: () => void;
    readonly onSwapsChanged?: () => void;
  } = {},
): LiveBoard {
  const queryClient = useQueryClient();
  const key = ['board', stayId] as const;
  const board = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<Board>(`/stays/${stayId}/board`)).data,
    enabled: stayId !== '',
  });

  // The latest version and options, for the event handlers. Updated after each render (a layout effect), never
  // during it: a render React throws away must not leave its values behind (rules of React, react-hooks/refs).
  const versionRef = useRef(0);
  const optionsRef = useRef(options);
  const version = board.data?.version ?? 0;
  useLayoutEffect(() => {
    versionRef.current = version;
    optionsRef.current = options;
  });

  const invalidateDerived = useCallback(() => {
    for (const derived of optionsRef.current.alsoInvalidate ?? []) {
      void queryClient.invalidateQueries({ queryKey: derived });
    }
  }, [queryClient]);

  const resync = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['board', stayId] });
    invalidateDerived();
  }, [queryClient, stayId, invalidateDerived]);

  const connection = useStayEvents(
    stayId === '' ? undefined : stayId,
    {
      getVersion: () => versionRef.current,
      onSlotsChanged: (event) => {
        const current = queryClient.getQueryData<Board>(['board', stayId]);
        const result = current === undefined ? undefined : applySlotChanges(current, event);
        if (current === undefined || result === undefined || result.kind === 'resync') {
          resync();
          return;
        }
        versionRef.current = result.board.version;
        queryClient.setQueryData(['board', stayId], result.board);
        const mine = mySlotId(current);
        const touchedMine = event.slots.some((slot) => slot.state === 'MINE' || slot.slotId === mine);
        if (touchedMine || optionsRef.current.invalidateOnAnyChange === true) invalidateDerived();
      },
      onResync: resync,
      onSessionEnded: () => optionsRef.current.onSessionEnded?.(),
      onSwapsChanged: () => optionsRef.current.onSwapsChanged?.(),
    },
    api.baseUrl,
  );

  return { board, connection, resync };
}
